/**
 * Shared Card Field Helpers
 *
 * Normalized profile values are not type-coerced by the API and any field may be
 * null or missing, so cards read them through these helpers instead of assuming
 * a string or an array.
 */

/**
 * Converts a scalar value to trimmed text. Arrays yield their first non-empty item.
 *
 * @param {*} value
 * @returns {string} Text, or empty string when the value holds no text.
 */
export function asText(value) {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const text = asText(entry);
      if (text) return text;
    }
    return '';
  }
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && isFinite(value)) return String(value);
  return '';
}

/**
 * Converts a value to a list of non-empty strings. A single string becomes a one-item list.
 *
 * @param {*} value
 * @returns {string[]}
 */
export function asList(value) {
  const items = Array.isArray(value) ? value.flat(Infinity) : [value];
  return items.map(asText).filter(Boolean);
}

/**
 * Returns the first non-empty text among the given keys, read from the profile
 * first and from the item itself second.
 *
 * @param {Object} item - The resource item.
 * @param {...string} keys - Field names, in priority order.
 * @returns {string}
 */
export function pickText(item, ...keys) {
  const prof = item?.profile || {};
  for (const key of keys) {
    const text = asText(prof[key]) || asText(item?.[key]);
    if (text) return text;
  }
  return '';
}

/**
 * Returns the first non-empty list among the given keys (profile first, item second).
 *
 * @param {Object} item - The resource item.
 * @param {...string} keys - Field names, in priority order.
 * @returns {string[]}
 */
export function pickList(item, ...keys) {
  const prof = item?.profile || {};
  for (const key of keys) {
    const list = asList(prof[key]);
    if (list.length) return list;
    const itemList = asList(item?.[key]);
    if (itemList.length) return itemList;
  }
  return [];
}

/**
 * Translates a label key, falling back to the given text when no translation exists.
 *
 * @param {Object} ctx - View context containing `t`.
 * @param {string} key - Translation key.
 * @param {string} fallback - Text used when the key is not translated.
 * @returns {string}
 */
export function label(ctx, key, fallback) {
  const text = ctx?.t?.(key);
  return text && text !== key ? text : fallback;
}

/**
 * Builds a single-line address from the street address, city, state and ZIP code.
 *
 * @param {Object} item - The resource item.
 * @returns {string} e.g. "400 20th St, Virginia Beach, VA 23451".
 */
export function buildFullAddress(item) {
  const address = pickText(item, 'address').replace(/\\r\\n|\r\n|\n/g, ', ');
  const city = pickText(item, 'city');
  const stateZip = [pickText(item, 'state'), pickText(item, 'zip_code')].filter(Boolean).join(' ');

  // The street line often already contains the city; do not repeat it.
  const parts = [address];
  if (city && !address.toLowerCase().includes(city.toLowerCase())) parts.push(city);
  if (stateZip && !address.includes(stateZip)) parts.push(stateZip);
  return parts.filter(Boolean).join(', ');
}
